import { getCurrentTenantId } from '@/lib/tenant';
import axios from 'axios';

/**
 * ElasticSearch Sync Service API Client
 * This client communicates with the ElasticSearch sync service for enhanced search functionality
 */
export const searchServiceAxios = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_SEARCH_SERVICE_URL ||
    'http://localhost:3002/v1/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout for long narrative queries
});

// Add request interceptor for tenant security headers
searchServiceAxios.interceptors.request.use(
  (config) => {
    const tenantId = getCurrentTenantId();

    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    } else {
      // Use default tenant for public searches
      const defaultTenantId =
        process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || 'public';
      config.headers['X-Tenant-ID'] = defaultTenantId;
      console.log(
        `Using default tenant ID for public search: ${defaultTenantId}`
      );
    }

    // Add timestamp for request tracking
    config.headers['X-Request-Time'] = new Date().toISOString();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and logging
searchServiceAxios.interceptors.response.use(
  (response) => {
    // Log successful search requests for analytics (optional)
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Search Service API Success: ${response.config.url}`, {
        status: response.status,
        data: response.data?.success,
        timestamp: new Date().toISOString(),
      });
    }
    return response;
  },
  (error) => {
    // Enhanced error handling for different scenarios
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      console.error(`🚨 Search Service API Error [${status}]:`, {
        url: error.config?.url,
        status,
        message: data?.error || error.message,
        tenantId: error.config?.headers?.['X-Tenant-ID'],
        timestamp: new Date().toISOString(),
      });

      // Handle specific error cases
      switch (status) {
        case 400:
          if (
            data?.code === 'MISSING_TENANT_ID' ||
            data?.code === 'INVALID_TENANT_ID'
          ) {
            console.error('🔒 Tenant security violation detected');
          }
          break;
        case 429:
          console.warn('⚠️ Rate limit exceeded for search requests');
          break;
        case 503:
          console.warn('⚠️ Search service temporarily unavailable');
          break;
      }
    } else if (error.request) {
      // Network error - service unreachable
      console.error('🌐 Search Service Network Error:', {
        message: 'ElasticSearch service unreachable',
        url: error.config?.url,
        timeout: error.code === 'ECONNABORTED',
        timeoutValue: error.config?.timeout,
        code: error.code,
        timestamp: new Date().toISOString(),
      });
    } else {
      // Request setup error
      console.error('⚙️ Search Service Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Health check function
export const checkSearchServiceHealth = async (): Promise<boolean> => {
  try {
    const response = await searchServiceAxios.get('/search/health');
    return response.data?.success === true;
  } catch (error) {
    console.warn('Search service health check failed:', error);
    return false;
  }
};

// Service availability check with caching
let serviceAvailableCache: { available: boolean; lastCheck: number } | null =
  null;
const CACHE_DURATION = 30000; // 30 seconds

export const isSearchServiceAvailable = (): boolean => {
  // For now, assume service is available if we can reach it
  // This is a synchronous check to avoid async issues in the keyword suggestions
  return true;
};

export const isSearchServiceAvailableAsync = async (): Promise<boolean> => {
  const now = Date.now();

  // Return cached result if recent
  if (
    serviceAvailableCache &&
    now - serviceAvailableCache.lastCheck < CACHE_DURATION
  ) {
    return serviceAvailableCache.available;
  }

  // Check service health
  const available = await checkSearchServiceHealth();

  // Cache result
  serviceAvailableCache = {
    available,
    lastCheck: now,
  };

  return available;
};

export default searchServiceAxios;
